import { Component, Inject, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormBuilder, FormsModule, Validators, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { FuseAlertComponent, FuseAlertType } from '@fuse/components/alert';
import { SharedMethodsModule } from 'app/core/shared-services/shared-methods.module';
import { ILead } from 'app/core/shared-services/shared-interface.types';
import { GtmService } from 'app/core/shared-services/gtm.service';

interface DialogData {
  packageInfo: any;
  userGoogleAddress: any;
}

@Component({
  standalone: true,
  imports: [
    FuseAlertComponent,
    FormsModule,
    ReactiveFormsModule,
    SharedMethodsModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    CommonModule
  ],
  selector: 'app-showmyinterest',
  templateUrl: './showmyinterest.component.html',
  styleUrls: ['./showmyinterest.component.scss'],
})
export class ShowmyinterestComponent implements OnInit {
  public dialogRef = inject(MatDialogRef<ShowmyinterestComponent>);

  alert: { type: FuseAlertType; message: string } = {
    type: 'success',
    message: '',
  };

  public userGoogleAddress: any;
  public callBackLeadResult: any;
  public packageInfo: any;
  public showAlert: boolean;
  public submissionSuccess: boolean = false;
  public referenceNumber: string;

  private lead: ILead;

  public submitButtonDisabled: boolean;

  @ViewChild('interestNgForm') interestNgForm: NgForm;
  public interestForm: UntypedFormGroup;

  constructor(
    private _fb: FormBuilder,
    private _router: Router,
    private _sharedMethodsModule: SharedMethodsModule,
    private _gtmService: GtmService,
    @Inject(MAT_DIALOG_DATA) public data: DialogData
  ) {
    this.packageInfo = data.packageInfo;
    this.userGoogleAddress = data.userGoogleAddress;
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  async ngOnInit() {

    // Matrix: show_my_interest fires on /show-my-interest page load.
    this._gtmService.trackShowMyInterest(this.packageInfo);

    // Anonymous visitors on /packages have no customer account — this must never
    // break form construction.
    const customer = (await this._sharedMethodsModule.getCustomerAccount())?.result;

    this.interestForm = this._fb.group({
      LeadContactName: ['', [Validators.required, Validators.maxLength(50)]],
      LeadContactSurname: ['', [Validators.required, Validators.maxLength(50)]],
      LeadContactEmail: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
      LeadContactNumber: ['', [Validators.required, Validators.maxLength(12)]]
    });

    this.interestForm.patchValue({
          LeadContactName: customer?.PrimaryContactName ?? '',
          LeadContactSurname: customer?.PrimaryContactLastName ?? '',
          LeadContactEmail: customer?.PrimaryContactEmail ?? '',
          LeadContactNumber: customer?.PrimaryContactMobile ?? '',
        });
  }

  private getStoredCoordinates(): any {
    try {
      return JSON.parse(sessionStorage.getItem('CustomerCoordinates')) || null;
    } catch {
      return null;
    }
  }

  // Public Methods
  public async Submit() {
    this.submitButtonDisabled = true;
    this.showAlert = false;

    try {
      const contact = {
        LeadContactName: this.interestForm.value.LeadContactName,
        LeadContactSurname: this.interestForm.value.LeadContactSurname,
        LeadContactEmail: this.interestForm.value.LeadContactEmail,
        LeadContactNumber: this.interestForm.value.LeadContactNumber
      };

      // Do not reuse the active order ID: POST /lead updates any ID in its payload.
      let leadEntityId: number;

      // The new callback lead, rather than the active order, receives any selected package.

      const createResult = await this._sharedMethodsModule.createCallbackLead(
        contact,
        this.getStoredCoordinates(),
        this.userGoogleAddress
      );

      if (!createResult.success || !createResult.result?.LeadEntityID) {
        this.alert = {
          type: 'error',
          message: createResult.error?.error?.Message || 'Something went wrong. Please try again.'
        };
        this.showAlert = true;
        return;
      }

      this.lead = createResult.result;
      leadEntityId = this.lead.LeadEntityID;

      // Add the package to the lead — only when a package was selected.
      // No-coverage / no-feasibility callbacks have no package to attach.
      if (this.packageInfo?.PackageEntityID) {
        const addItemResult: any = await this._sharedMethodsModule.addPackageToLead(
          leadEntityId,
          this.packageInfo.PackageEntityID
        );

        if (!addItemResult.success) {
          this.alert = {
            type: 'error',
            message: addItemResult.error?.error?.Message || 'Failed to add package to lead'
          };
          this.showAlert = true;
          return;
        }
      }

      // Raises the "Callback Request" notification to the sales team. Best effort —
      // the lead itself is already captured, so never fail the submission on this.
      await this._sharedMethodsModule.requestCallBack(leadEntityId);

      this.referenceNumber = leadEntityId?.toString() || '';
      this.gtmEvent();

      this.submissionSuccess = true;
    } catch (err) {
      this.alert = {
        type: 'error',
        message: 'Something went wrong. Please try again.'
      };
      this.showAlert = true;
    } finally {
      // Always release the button so the dialog can never get stuck on "Submitting...".
      this.submitButtonDisabled = false;
    }
  }

  public close(): void {
    this.gtmCancelEvent();
    this.dialogRef.close({ success: false });
  }

  public closeThankYou(): void {
    sessionStorage.removeItem('packageInfo');
    sessionStorage.removeItem('AvailablePackages');
    sessionStorage.removeItem('CustomerAddress');
    sessionStorage.removeItem('CustomerCoordinates');
    sessionStorage.removeItem('CustomerGoogleAddress');
    sessionStorage.removeItem('LeadEntityID');

    this.dialogRef.close({ success: true });
    this._router.navigateByUrl('/');
  }

  private gtmCancelEvent(): void {
    const productDetails = this.packageInfo ? this._gtmService.buildEcommerceItem(this.packageInfo) : undefined;
    this._gtmService.trackCancelledLead(productDetails);
  }

  private gtmEvent() {
    const productDetails = this.packageInfo ? this._gtmService.buildEcommerceItem(this.packageInfo) : undefined;
    const value = this.packageInfo?.CoreOfferable?.Billing_PriceIncl ?? this.packageInfo?.Billing_TotalIncl ?? 0;
    this._gtmService.trackGenerateLead(this.referenceNumber ?? '', value, productDetails);
  }
}
